import { useState } from "react";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { StoreBadges } from "@/components/marketing/StoreBadges";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import GuideSidebar from "@/components/fieldguide/GuideSidebar";
import GuideSection, { GuideSubSection } from "@/components/fieldguide/GuideSection";
import StepList from "@/components/fieldguide/StepList";
import InfoCard from "@/components/fieldguide/InfoCard";
import FeatureCard from "@/components/fieldguide/FeatureCard";
import { PageTransition } from "@/components/PageTransition";
import {
  Calendar,
  Clock,
  FileText,
  MapPin,
  Wifi,
  WifiOff,
  Bell,
  User,
  CheckCircle,
} from "lucide-react";

const crewSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    items: [
      { id: "invitation", title: "Receiving Your Invitation" },
      { id: "account", title: "Creating Your Account" },
      { id: "install", title: "Installing the App" },
      { id: "first-login", title: "First Login" },
    ],
  },
  {
    id: "home-screen",
    title: "Home Screen",
    items: [
      { id: "overview", title: "Overview" },
      { id: "production-info", title: "Production Information" },
      { id: "updates", title: "Timeline Updates" },
      { id: "crew-list", title: "Crew List" },
    ],
  },
  {
    id: "your-schedule",
    title: "Your Schedule",
    items: [
      { id: "viewing", title: "Viewing Your Schedule" },
      { id: "assignments", title: "Understanding Assignments" },
      { id: "task-details", title: "Task Details" },
    ],
  },
  {
    id: "timetable",
    title: "Timetable",
    items: [
      { id: "full-lineup", title: "Full Lineup" },
      { id: "stage-filter", title: "Stage Filtering" },
      { id: "artist-info", title: "Artist Information" },
    ],
  },
  {
    id: "briefing",
    title: "Briefing",
    items: [
      { id: "event-details", title: "Event Details" },
      { id: "catering", title: "Catering & Facilities" },
      { id: "equipment", title: "Equipment Settings" },
      { id: "festival-map", title: "Festival Map" },
    ],
  },
  {
    id: "live-tracking",
    title: "Live Tracking",
    items: [
      { id: "map-view", title: "Map View" },
      { id: "your-location", title: "Your Location" },
      { id: "other-crew", title: "Finding Other Crew" },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    items: [
      { id: "notifications", title: "Notifications" },
      { id: "location", title: "Location Settings" },
      { id: "offline", title: "Offline Data" },
      { id: "account", title: "Account" },
    ],
  },
  {
    id: "offline-mode",
    title: "Working Offline",
    items: [
      { id: "what-works", title: "What Works Offline" },
      { id: "sync", title: "Data Synchronization" },
    ],
  },
];

const CrewGuide = () => {
  useScrollToHash();
  const [activeSection, setActiveSection] = useState<string>("");

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <div className="flex">
          <GuideSidebar
            sections={crewSections}
            activeSection={activeSection}
            onSectionClick={setActiveSection}
            guideType="crew"
          />

          <main className="max-w-4xl flex-1 p-8 lg:p-12">
            <div className="mb-12">
              <h1 className="mb-4 text-4xl font-bold">Crew member guide</h1>
              <p className="text-xl text-muted-foreground">
                Everything you need to know about using the PROduce crew app during productions.
              </p>
            </div>

            <div className="space-y-16">
              {/* Getting Started */}
              <GuideSection
                id="getting-started-invitation"
                title="Getting Started"
                description="How to access PROduce and set up your account as a crew member."
              >
                <GuideSubSection id="getting-started-invitation" title="Receiving Your Invitation">
                  <p className="mb-4 text-muted-foreground">
                    When you're added to a production, you'll receive an invitation email from the
                    producer.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Check your email",
                        description:
                          "Look for an email from PROduce with the subject containing the production name.",
                      },
                      {
                        title: "Click the access link",
                        description: "The email contains a unique link to access the production.",
                      },
                      {
                        title: "Don't see it?",
                        description:
                          "Check your spam folder. If you still can't find it, contact your producer.",
                      },
                    ]}
                  />
                  <InfoCard variant="tip" title="Email Address">
                    Make sure the producer has your correct email address. If it's wrong, ask them
                    to update it and resend the invitation.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="getting-started-account" title="Creating Your Account">
                  <p className="mb-4 text-muted-foreground">
                    The first time you use PROduce, you'll need to create an account.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Click the invitation link",
                        description:
                          "This takes you to the login page with the production pre-selected.",
                      },
                      {
                        title: "Create a password",
                        description: "Choose a secure password you'll remember.",
                      },
                      {
                        title: "Verify your email",
                        description: "Click the verification link sent to your email.",
                      },
                      {
                        title: "You're in!",
                        description: "Once verified, you have full access to the production.",
                      },
                    ]}
                  />
                  <InfoCard variant="info" title="Returning Users">
                    If you've used PROduce before, just log in with your existing account. The new
                    production will automatically appear in your app.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="getting-started-install" title="Getting the App">
                  <p className="mb-4 text-muted-foreground">
                    PROduce Crew is a native app. Install it from your phone&apos;s app store —
                    there is no web version to log into.
                  </p>
                  <div className="mb-6 flex justify-center">
                    <StoreBadges size="lg" className="max-w-md" />
                  </div>
                  <InfoCard variant="tip" title="Why the app?">
                    Push notifications the moment your schedule changes, location sharing with your
                    producer while you are on site, and your schedule and briefing available offline
                    — none of which a browser can do.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="getting-started-first-login" title="First Login">
                  <p className="mb-4 text-muted-foreground">
                    After logging in, you'll see the home screen with your production information.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Enable notifications",
                        description:
                          "Allow push notifications when prompted to receive assignment reminders.",
                      },
                      {
                        title: "Enable location",
                        description:
                          "If your producer uses live tracking, you'll be asked to share your location.",
                      },
                      {
                        title: "Explore the app",
                        description:
                          "Navigate using the bottom menu to familiarize yourself with the app.",
                      },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Home Screen */}
              <GuideSection
                id="home-screen-overview"
                title="Home Screen"
                description="Your central hub for production information."
              >
                <GuideSubSection id="home-screen-overview" title="Overview">
                  <p className="mb-4 text-muted-foreground">
                    The home screen shows the most important information about your current
                    production.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FeatureCard
                      icon={FileText}
                      title="Production Name"
                      description="The event you're working on, with dates and location."
                    />
                    <FeatureCard
                      icon={Clock}
                      title="Next Assignment"
                      description="Your upcoming task with countdown timer."
                    />
                    <FeatureCard
                      icon={Bell}
                      title="Updates"
                      description="Any recent changes or announcements."
                    />
                    <FeatureCard
                      icon={User}
                      title="Your Info"
                      description="Your role and assigned days."
                    />
                  </div>
                </GuideSubSection>

                <GuideSubSection id="home-screen-production-info" title="Production Information">
                  <p className="mb-4 text-muted-foreground">
                    The production card shows essential event details.
                  </p>
                  <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span className="text-sm">Event dates and days you're scheduled</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-primary" />
                      <span className="text-sm">Venue name and location</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="text-sm">Doors open/close times</span>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="home-screen-updates" title="Timeline Updates">
                  <p className="mb-4 text-muted-foreground">
                    The updates timeline shows recent changes to the production.
                  </p>
                  <InfoCard variant="info">
                    When schedules change, you'll see updates here and receive a push notification
                    (if enabled).
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="home-screen-crew-list" title="Crew List">
                  <p className="mb-4 text-muted-foreground">
                    View all other crew members working on the production.
                  </p>
                  <StepList
                    steps={[
                      { title: "Tap crew member", description: "See their role and schedule." },
                      {
                        title: "View their schedule",
                        description: "Check when they're working which assignments.",
                      },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Your Schedule */}
              <GuideSection
                id="your-schedule-viewing"
                title="Your Schedule"
                description="View and understand your personal assignment schedule."
              >
                <GuideSubSection id="your-schedule-viewing" title="Viewing Your Schedule">
                  <p className="mb-4 text-muted-foreground">
                    Your schedule shows all your assignments for the production.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Navigate to Schedule",
                        description: "Tap 'Schedule' in the bottom navigation.",
                      },
                      {
                        title: "Select day",
                        description: "If multi-day event, tap the day tabs to switch between days.",
                      },
                      {
                        title: "Timeline view",
                        description: "Scroll through the timeline to see all assignments.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="your-schedule-assignments" title="Understanding Assignments">
                  <p className="mb-4 text-muted-foreground">
                    Each assignment block shows key information at a glance.
                  </p>
                  <div className="space-y-4">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-3 font-medium">Assignment Information</h4>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <p>
                          <strong>Time:</strong> Start and end time of the assignment
                        </p>
                        <p>
                          <strong>Artist/Task:</strong> Who or what you're covering
                        </p>
                        <p>
                          <strong>Stage:</strong> Where you need to be
                        </p>
                        <p>
                          <strong>Notes:</strong> Any special instructions
                        </p>
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="flex items-center gap-3">
                        <div className="h-4 w-4 rounded bg-muted"></div>
                        <span className="text-sm">Upcoming</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-4 w-4 rounded bg-primary"></div>
                        <span className="text-sm">In Progress</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-4 w-4 rounded bg-muted-foreground/30"></div>
                        <span className="text-sm">Completed</span>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="your-schedule-task-details" title="Task Details">
                  <p className="mb-4 text-muted-foreground">
                    Tap any assignment to see full details.
                  </p>
                  <StepList
                    steps={[
                      { title: "Tap assignment", description: "Opens the task detail sheet." },
                      {
                        title: "View all info",
                        description: "See complete details, notes, and stage information.",
                      },
                      {
                        title: "Navigate to stage",
                        description: "Quick link to the stage location on the map.",
                      },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Timetable */}
              <GuideSection
                id="timetable-full-lineup"
                title="Timetable"
                description="View the complete event lineup."
              >
                <GuideSubSection id="timetable-full-lineup" title="Full Lineup">
                  <p className="mb-4 text-muted-foreground">
                    The timetable shows all artists and performances across all stages.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="timetable-stage-filter" title="Stage Filtering">
                  <p className="mb-4 text-muted-foreground">
                    Filter the timetable to show only specific stages.
                  </p>
                  <StepList
                    steps={[
                      { title: "Tap stage filter", description: "At the top of the timetable." },
                      { title: "Select stages", description: "Choose which stages to display." },
                      {
                        title: "Apply filter",
                        description: "The timetable updates to show only selected stages.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="timetable-artist-info" title="Artist Information">
                  <p className="mb-4 text-muted-foreground">
                    Tap any artist to see their set details.
                  </p>
                </GuideSubSection>
              </GuideSection>

              {/* Briefing */}
              <GuideSection
                id="briefing-event-details"
                title="Briefing"
                description="Access all production information and instructions."
              >
                <GuideSubSection id="briefing-event-details" title="Event Details">
                  <p className="mb-4 text-muted-foreground">
                    The briefing contains all essential information about the production.
                  </p>
                  <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Check-in Time & Location</h4>
                        <p className="text-sm text-muted-foreground">Where and when to arrive</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Dress Code</h4>
                        <p className="text-sm text-muted-foreground">What to wear</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">General Instructions</h4>
                        <p className="text-sm text-muted-foreground">
                          Important notes from the producer
                        </p>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="briefing-catering" title="Catering & Facilities">
                  <p className="mb-4 text-muted-foreground">
                    View catering times, locations, and available facilities.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="briefing-equipment" title="Equipment Settings">
                  <p className="mb-4 text-muted-foreground">
                    Camera settings, audio configurations, and any restrictions.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="briefing-festival-map" title="Festival Map">
                  <p className="mb-4 text-muted-foreground">
                    View the event map with all stages and important locations marked.
                  </p>
                  <StepList
                    steps={[
                      { title: "View map", description: "Interactive map of the venue." },
                      {
                        title: "Find stages",
                        description: "Tap stage zones to see names and details.",
                      },
                      {
                        title: "Get directions",
                        description: "Use the navigation button to get walking directions.",
                      },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Live Tracking */}
              <GuideSection
                id="live-tracking-map-view"
                title="Live Tracking"
                description="Navigate the event and find your team."
              >
                <GuideSubSection id="live-tracking-map-view" title="Map View">
                  <p className="mb-4 text-muted-foreground">
                    The live tracking map shows your position and other crew members in real-time.
                  </p>
                  <InfoCard variant="info" title="Location Sharing">
                    You can control whether your location is shared with the producer in Settings.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="live-tracking-your-location" title="Your Location">
                  <p className="mb-4 text-muted-foreground">
                    Your current position is shown as a highlighted marker on the map.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Center on you",
                        description: "Tap the location button to center the map on your position.",
                      },
                      {
                        title: "Stage detection",
                        description:
                          "When you enter a stage zone, the app recognizes you're at that stage.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="live-tracking-other-crew" title="Finding Other Crew">
                  <p className="mb-4 text-muted-foreground">
                    See where other crew members are located on the map.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "View crew markers",
                        description: "Other crew appear as colored dots with initials.",
                      },
                      {
                        title: "Tap for details",
                        description: "See who it is and their current assignment.",
                      },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Settings */}
              <GuideSection
                id="settings-notifications"
                title="Settings"
                description="Configure your app preferences."
              >
                <GuideSubSection id="settings-notifications" title="Notifications">
                  <p className="mb-4 text-muted-foreground">
                    Control which notifications you receive.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Bell className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Assignment Reminders</h4>
                        <p className="text-sm text-muted-foreground">
                          Get notified before your assignments start
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Bell className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Schedule Changes</h4>
                        <p className="text-sm text-muted-foreground">
                          Be alerted when your schedule is updated
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Bell className="mt-0.5 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Announcements</h4>
                        <p className="text-sm text-muted-foreground">
                          Important messages from the producer
                        </p>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="settings-location" title="Location Settings">
                  <p className="mb-4 text-muted-foreground">
                    Control how and when your location is shared.
                  </p>
                  <InfoCard variant="warning" title="Battery Usage">
                    Background location tracking may increase battery usage. Consider carrying a
                    power bank during long events.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="settings-offline" title="Offline Data">
                  <p className="mb-4 text-muted-foreground">
                    Manage offline data storage for areas with poor connectivity.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="settings-account" title="Account">
                  <p className="mb-4 text-muted-foreground">
                    Manage your account settings and preferences.
                  </p>
                </GuideSubSection>
              </GuideSection>

              {/* Offline Mode */}
              <GuideSection
                id="offline-mode-what-works"
                title="Working Offline"
                description="What to expect when you lose connectivity."
              >
                <GuideSubSection id="offline-mode-what-works" title="What Works Offline">
                  <p className="mb-4 text-muted-foreground">
                    PROduce caches essential data so you can continue working without internet.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <Wifi className="h-5 w-5 text-primary" />
                        <h4 className="font-medium">Available Offline</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• Your schedule and assignments</li>
                        <li>• Briefing information</li>
                        <li>• Timetable and lineup</li>
                        <li>• Downloaded map tiles</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <WifiOff className="h-5 w-5 text-muted-foreground" />
                        <h4 className="font-medium">Requires Internet</h4>
                      </div>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• Real-time updates</li>
                        <li>• Seeing other crew locations</li>
                        <li>• Sending your location</li>
                        <li>• Push notifications</li>
                      </ul>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="offline-mode-sync" title="Data Synchronization">
                  <p className="mb-4 text-muted-foreground">
                    When you're back online, the app automatically syncs any pending data.
                  </p>
                  <InfoCard variant="tip" title="Pre-download">
                    For events with known connectivity issues, open the app while on WiFi to cache
                    all data before heading to the venue.
                  </InfoCard>
                </GuideSubSection>
              </GuideSection>
            </div>
          </main>
        </div>
      </div>
    </PageTransition>
  );
};

export default CrewGuide;
